/**
 * RegisterPage Component
 *
 * Public registration page for new users to sign up for a subscription.
 * Reads plan and interval from URL params.
 *
 * Flow:
 * 1. User arrives from /subscribe with plan & interval in URL params
 * 2. User fills name and email
 * 3. Submit creates pending_registration via Edge Function
 * 4. Redirect to /check-email
 *
 * Project: P07 - Stripe Integration
 * Sprint: S05 - Polish & Production Readiness
 */

import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { RegistrationForm } from './RegistrationForm';
import type { PlanType, BillingInterval } from '@enfomentis/shared/types';
import { DefaultPlan, DefaultBillingInterval } from '@enfomentis/shared/config';

export function RegisterPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get plan from URL params
  const planParam = searchParams.get('plan') as PlanType | null;
  const intervalParam = searchParams.get('interval') as BillingInterval | null;

  const planType: PlanType =
    planParam && (planParam === 'pro' || planParam === 'pro_plus')
      ? planParam
      : DefaultPlan;
  const billingInterval: BillingInterval =
    intervalParam && (intervalParam === 'month' || intervalParam === 'year')
      ? intervalParam
      : DefaultBillingInterval;

  // Redirect to subscribe if no plan selected
  useEffect(() => {
    if (!planParam) {
      navigate('/subscribe', { replace: true });
    }
  }, [planParam, navigate]);

  // Handle back navigation
  const handleBack = () => {
    const params = new URLSearchParams({
      plan: planType,
      interval: billingInterval,
    });
    navigate(`/subscribe?${params.toString()}`);
  };

  // Show nothing while redirecting
  if (!planParam) {
    return <></>;
  }

  return (
    <RegistrationForm
      planType={planType}
      billingInterval={billingInterval}
      onBack={handleBack}
    />
  );
}
